import { Group } from "@fairfleet/geotab";

export interface AccessLogbookGroupSettings {
  /** The id of the `Access Logbook` group. */
  groupId: string;
  /** The number of days to show in the logbook. */
  numberOfDays: number;
  /** The list of trip reasons that can be used when submitting driver log entries. */
  tripReasons: string[];
}

/**
 * Parses {@link AccessLogbookGroupSettings} from the comments of the given {@link Group}.
 *
 * @remarks
 *
 * This is intended for use with a group named `Access Logbook` within the `Fairfleet Services`
 * group.  In the future we should probably utilize Geotab Add-In settings for this.
 */
export default function parseGroupSettings(group: Group | null) {
  if (!group?.comments) {
    return {
      groupId: group?.id ?? "",
      numberOfDays: 30,
      tripReasons: [],
    } as AccessLogbookGroupSettings;
  }

  const parts = group.comments.split("\n");
  if (parts.length === 0) {
    throw new Error("Access Logbook parse failed.");
  }

  // Line 1 is the number of days, every further line holds comma-separated reasons. Same rules
  // as the EcoApp's fetchLogbookAccess: lines 2+ are joined, blanks and the header word dropped.
  const numberOfDaysPart = parts.length > 1 ? parts[0] : "7";
  const tripReasonsPart = parts.length > 1 ? parts.slice(1).join("\n") : parts[0];

  const numberOfDays = parseInt(numberOfDaysPart, 10);
  const tripReasons = tripReasonsPart
    .split(",")
    .map(r => r.trim())
    .filter(r => r.length > 0 && r !== "Fahrtgrund");

  return {
    groupId: group.id,
    numberOfDays,
    tripReasons,
  } as AccessLogbookGroupSettings;
}
